import './styles.scss'
import { BarGraph, Grid, LegendItemProps } from '@/components'
import formatClasses from '@/utils/classes/formatClasses'
import getColor from '@/utils/color/getColor'
import React, { useEffect, useState, useMemo } from 'react'
import { positiveClamp } from '@/utils/limit'
import { isDecimal } from '@/utils/number'
import {
  ThermostatCostSavingsDataPeriod,
  ThermostatCostSavingsViewModel,
} from '../useThermostatCostSavingsViewModel'
import ThermostatSummaryCard from './ThermostatSummaryCard'
import { centsToDollars } from '@/utils/currency'
import dayjs from 'dayjs'
import { DateRepresentation } from '@/types/dates'
import { ThermostatUsageInterval } from '@/types/thermostatCostHistorySummary'

export type DataPointType = 'cool' | 'heat' | 'adjustments'

export interface DataPoint {
  cost: number
  data: { value: number; type: DataPointType }[]
  label: string
  maxValue: number
  type: ThermostatCostSavingsDataPeriod
  value: number // # of kWh
}

interface DataPointProps extends DataPoint {
  onPointerDown: (event: React.PointerEvent<HTMLElement>) => void
  onPointerEnter: (info: HoveredDataPointInfo) => void
  selected: boolean
  setSelected: (selected: boolean) => void
}

export interface HoveredDataPointInfo {
  energyUsed: number
  totalSpent: number
  period: string
}

function DataPoint(props: DataPointProps) {
  const classes = {
    bar: formatClasses(['total-spent-bar-graph-bar']),
    dataPoint: formatClasses([
      'total-spent-data-point',
      props.selected ? 'selected' : undefined,
    ]),
  }

  const BAR_GRAPH_HEIGHT = 105 // Height in pixels

  // Sum of all data values for this data point (cooling + heating + adjustments)
  const totalValue = props.data
    .map((item) => Number(item.value.toFixed(1)))
    .reduce((sum, operand) => sum + operand, 0)

  // Computed height of this bar
  const height = `${Math.round(
    (totalValue / props.maxValue) * BAR_GRAPH_HEIGHT,
  )}px`

  function getBackground() {
    const colors = {
      cool: 'blue-500',
      heat: 'red-500',
      adjustments: 'orange-300',
      off: 'grey-200',
    }

    if (totalValue <= 0) {
      return getColor(colors.off)
    }

    const filteredData = props.data.filter((item) => item.value)

    // If there's only one piece of data, use a solid color, otherwise generate a gradient
    if (filteredData.length === 1) {
      return getColor(colors[filteredData[0].type])
    } else {
      const sortOrder = {
        cool: 3,
        heat: 2,
        adjustments: 1,
        off: 3,
      }

      // Sort in the order of Cool, Heat, Adjustment, starting from the bottom of the bar
      const sortedData = filteredData.sort((a, b) => {
        return sortOrder[a.type] - sortOrder[b.type]
      })

      // Create the linear gradient for the bar's background color
      // First, filter and validate all items to ensure we only process valid ones
      const validItems = sortedData
        .map((item) => {
          if (item.value <= 0) return null
          if (!(item.type in colors)) return null
          const colorKey = colors[item.type as keyof typeof colors]
          if (!colorKey) return null
          const color = getColor(colorKey)
          if (!color || typeof color !== 'string') return null
          return { ...item, color }
        })
        .filter(
          (item): item is (typeof sortedData)[0] & { color: string } =>
            item !== null,
        )

      if (validItems.length === 0) {
        return getColor(colors.off) || 'transparent'
      }

      let cumulativeValue = 0
      const gradientStops = validItems.map((item, index) => {
        // Percentage to begin the color at (cumulative of previous items)
        const startPercent = (cumulativeValue / totalValue) * 100
        // Add current item's value to cumulative
        cumulativeValue += item.value
        // Percentage to end the color at (cumulative including current item)
        // For the last item, ensure it ends at exactly 100%
        const endPercent =
          index === validItems.length - 1
            ? 100
            : (cumulativeValue / totalValue) * 100

        return `${item.color} ${startPercent}% ${endPercent}%`
      })

      return `linear-gradient(to bottom, ${gradientStops.join(', ')})`
    }
  }

  function onPointerEnter() {
    props.setSelected(true)

    const label = props.label
      ? props.type !== 'day'
        ? props.label
        : `Today between ${props.label}`
      : ''

    // Update the summary to show the data for the selected bar
    props.onPointerEnter({
      energyUsed: totalValue,
      period: label,
      totalSpent: props.cost,
    })
  }

  return (
    <div
      className={classes.dataPoint}
      data-key={props.label ? props.label : undefined}
      data-type={props.type}
      data-value={totalValue}
      onPointerDown={props.onPointerDown}
      onPointerEnter={onPointerEnter}
      onPointerLeave={() => props.setSelected(false)}
    >
      <div
        className={classes.bar}
        style={{
          background:
            totalValue !== 0 ? getBackground() : 'var(--color-grey-200)',
          height: height,
        }}
      />
    </div>
  )
}

const legend: LegendItemProps[] = [
  {
    label: 'cool',
    color: 'blue-500',
  },
  {
    label: 'heat',
    color: 'red-500',
  },
  {
    label: 'adjustments',
    color: 'orange-300',
  },
]

function onPointerDown(event: React.PointerEvent<HTMLElement>) {
  // @ts-expect-error hasPointerCapture does not exist on event.target
  if (event.target.hasPointerCapture(event.pointerId)) {
    // @ts-expect-error releasePointerCapture does not exist on event.target
    event.target.releasePointerCapture(event.pointerId)
  }
}

function generateLabel(
  date: DateRepresentation,
  period: ThermostatCostSavingsDataPeriod,
) {
  if (period !== 'day') return dayjs(date as string).format('ddd, MMM D')
  else {
    const nextHour = dayjs(date as string)
      .add(1, 'hour')
      .format()
    return `${dayjs(date as string).format('h')}–${dayjs(nextHour).format(
      'ha',
    )}`
  }
}

function formatUsageData(
  usageIntervals: ThermostatUsageInterval[],
  period: ThermostatCostSavingsDataPeriod,
) {
  const dataPoints: DataPoint[] = []
  let maxValue = 0

  usageIntervals.forEach((interval) => {
    const dataPoint = {} as DataPoint

    // @ts-expect-error date does not exist on type DataPoint
    dataPoint.date = interval.date
    dataPoint.label = generateLabel(interval.date, period)
    dataPoint.type = period
    dataPoint.value = Number(
      (
        interval.usage.adjustments.kwh_used +
        interval.usage.cool.kwh_used +
        interval.usage.heat.kwh_used
      ).toFixed(1),
    )
    dataPoint.data = Object.keys(interval.usage).map((key) => {
      const usageType = key as DataPointType

      return {
        value: Number(interval.usage[usageType].kwh_used.toFixed(1)),
        type: usageType,
      }
    })

    dataPoint.cost = Number(
      centsToDollars(
        interval.usage.adjustments.cost_cents +
          interval.usage.cool.cost_cents +
          interval.usage.heat.cost_cents,
      ).toFixed(2),
    )

    // Update max value
    if (dataPoint.value > maxValue) {
      maxValue = dataPoint.value
    }

    dataPoints.push(dataPoint)
  })

  // Update all data points with the highest value in the graph
  dataPoints.forEach((dataPoint) => {
    dataPoint.maxValue = maxValue || 1
  })

  // If the day does not have 24 hours, fill in the remaining hours with blank data
  if (period === 'day' && dataPoints.length < 24) {
    const hours = [...dataPoints, ...Array(24 - dataPoints.length)]

    dataPoints.length = 0
    dataPoints.push(
      ...hours.map((dataPoint, index) => {
        if (dataPoint) return dataPoint
        else {
          let label

          // If the data is not undefined and there is a previous element in the array...
          if (hours[0] && index !== 0) {
            // Generate a label using a date that continues from the last provided date
            const date = dayjs(hours[dataPoints.length - 1]?.date)
              .add(index - dataPoints.length + 1, 'hours')
              .format()

            label = generateLabel(date, 'day')
          } else {
            // Generate a label starting from the beginning of the day
            const date = dayjs().startOf('day').add(index, 'hours').format()

            label = generateLabel(date, 'day')
          }

          return {
            cost: 0,
            data: [
              {
                value: 0,
                type: 'cool',
              },
            ],
            label: hours[index]?.label || label,
            type: 'day',
            value: 0,
            maxValue: maxValue || 1,
          }
        }
      }),
    )
  }

  // Limit month to 30 data points
  if (period === 'month' && dataPoints.length > 30) {
    dataPoints.splice(0, dataPoints.length - 30)
  }

  return dataPoints
}

export default function ThermostatCostsBarGraph(
  props: ThermostatCostSavingsViewModel,
) {
  const items = useMemo(
    () => formatUsageData(props.usageIntervals, props.period),
    [props.usageIntervals, props.period],
  )

  const totalSpent = useMemo(
    () => centsToDollars(props.totalCostCents),
    [props.totalCostCents],
  )

  const [selectedBar, setSelectedBar] = useState<number | null>(null)
  const [selectedInfo, setSelectedInfo] = useState<HoveredDataPointInfo>({
    period: props.periodLabel,
    energyUsed:
      items
        .filter((item) => item.type === props.period)
        .reduce((sum, dataPoint) => sum + dataPoint.value, 0) || 0,
    totalSpent: totalSpent,
  })

  const maxValue = useMemo(() => {
    return items.find((item) => item.type === props.period)?.maxValue || 0
  }, [items, props.period])

  const xAxis = useMemo(() => {
    if (items.length === 0) {
      if (props.period === 'day') return ['12a', 'noon', '12a']
      if (props.period === 'week')
        return [
          dayjs().subtract(6, 'days').format('ddd'),
          dayjs().subtract(3, 'days').format('ddd'),
          dayjs().format('ddd'),
        ]
      return [
        dayjs().subtract(1, 'month').format('Do'),
        dayjs().subtract(15, 'days').format('Do'),
        dayjs().format('Do'),
      ]
    }

    if (items.length <= 3) {
      return items.map((item) => item.label)
    } else {
      const first = items[0]
      const middle = items[Math.floor(items.length / 2)]
      const last = items[items.length - 1]
      return [first.label, middle.label, last.label]
    }
  }, [items, props.period])

  const yAxis = useMemo(() => {
    return [maxValue || 0, (maxValue || 0) / 2, 'kWh']
  }, [maxValue])

  useEffect(() => {
    setSelectedInfo({
      period: props.periodLabel,
      energyUsed:
        items
          .filter((item) => item.type === props.period)
          .reduce((sum, dataPoint) => sum + dataPoint.value, 0) || 0,
      totalSpent: totalSpent,
    })
    setSelectedBar(null)
  }, [props.period, props.periodLabel, items, totalSpent])

  function onPointerEnter(info: HoveredDataPointInfo) {
    setSelectedInfo(info)
  }

  function onPointerLeave() {
    setSelectedInfo({
      period: props.periodLabel,
      energyUsed:
        items
          .filter((item) => item.type === props.period)
          .reduce((sum, dataPoint) => sum + dataPoint.value, 0) || 0,
      totalSpent: totalSpent,
    })
    setSelectedBar(null)
  }

  const clampedEnergyUsed = positiveClamp(selectedInfo.energyUsed)
  const energyDecimals =
    clampedEnergyUsed === 0 || clampedEnergyUsed > 100 ? 0 : 1
  const clampedTotalSpent = positiveClamp(selectedInfo.totalSpent)
  const totalSpentDecimals = isDecimal(clampedTotalSpent) ? 2 : 0

  function DataPoints() {
    // Only compute filler data when loading className is set and there is no data
    const shouldShowFillerData = props.loading && items.length === 0

    // Generate filler data points for loading animation when no data exists
    const getFillerDataPoints = (): DataPoint[] => {
      if (!shouldShowFillerData) return []

      const count =
        props.period === 'day' ? 24 : props.period === 'week' ? 7 : 30
      return Array.from({ length: count }, () => ({
        cost: 0,
        data: [{ value: 0, type: 'cool' as DataPointType }],
        label: '',
        maxValue: 1,
        type: props.period,
        value: 0,
      }))
    }

    const itemsToRender = shouldShowFillerData ? getFillerDataPoints() : items

    return (
      <>
        {itemsToRender.map((item, index) => {
          return (
            <DataPoint
              data={item.data}
              cost={item.cost}
              key={index}
              label={item.label}
              maxValue={item.maxValue}
              type={props.period}
              value={item.value}
              onPointerDown={onPointerDown}
              onPointerEnter={onPointerEnter}
              selected={index === selectedBar}
              setSelected={(selected: boolean) => {
                if (!selected) {
                  // Returns the graph back to its default state when the pointer leaves via the bottom of the graph
                  setSelectedInfo({
                    period: props.periodLabel,
                    energyUsed:
                      items
                        .filter((item) => item.type === props.period)
                        .reduce((sum, dataPoint) => sum + dataPoint.value, 0) ||
                      0,
                    totalSpent: totalSpent,
                  })

                  // Deselects the bar when the pointer leaves the graph on desktop
                  setSelectedBar(null)
                } else setSelectedBar(index)
              }}
            />
          )
        })}
      </>
    )
  }

  return (
    <>
      <Grid flow="row" gap="24px">
        <ThermostatSummaryCard
          period={selectedInfo.period}
          tagClassName="bg-themed-grey-100 rounded-lg p-4"
          loading={props.loading}
          left={{
            label: 'Energy Used',
            value: clampedEnergyUsed,
            decimals: energyDecimals,
            unit: 'kWh',
          }}
          right={{
            label: 'Total Spent',
            value: clampedTotalSpent,
            decimals: totalSpentDecimals,
            unit: '$',
            unitPosition: 'prefix',
          }}
        />
        <BarGraph
          className={props.loading ? 'loading' : undefined}
          onPointerLeave={onPointerLeave}
          legend={legend}
          xAxis={xAxis}
          yAxis={yAxis}
        >
          <DataPoints />
        </BarGraph>
      </Grid>
    </>
  )
}
